import { api } from '@/lib/api/client';
import { authFetch } from '@/api';
import type { Patient, DeceasedStatus } from './types';

/**
 * A candidate returned by POST /api/patients/check-duplicate — an MPI-style
 * match combining deterministic signals (exact mobile, exact DOB) with fuzzy
 * name similarity (see backend/routes/patients.js). Deliberately NOT `extends
 * Patient` (audit-flagged, L3): the backend trims its SELECT to exactly the
 * columns VerifyStage.tsx's match card renders — address, ABHA number,
 * emergency contact and everything else a full Patient row carries are never
 * sent for a query that's just "does this look like the same person," and
 * this type says so rather than claiming fields that aren't actually there.
 */
export interface DuplicateMatch {
  id: Patient['id'];
  uhid: Patient['uhid'];
  name: Patient['name'];
  age: Patient['age'];
  gender: Patient['gender'];
  mobile: Patient['mobile'];
  dob: Patient['dob'];
  score: number;
  confidence: 'high' | 'medium' | 'low';
  reasons: string[];
}

export interface DuplicateCheckInput {
  name?: string;
  mobile?: string;
  dob?: string | null;
  gender?: string | null;
  age?: number | null;
}

export function checkDuplicatePatient(input: DuplicateCheckInput): Promise<{ matches: DuplicateMatch[] }> {
  return api.post('/patients/check-duplicate', input);
}

/**
 * Every field routes/patients.js POST /create can persist — name/mobile/
 * gender are always mandatory; the rest map 1:1 onto OPTIONAL_PATIENT_FIELDS
 * on the backend. This is the complete set intake_form_config can ever
 * produce today: there is no UI or API to add a genuinely new intake field,
 * only to reorder/toggle these.
 */
export interface NewPatientInput {
  /** Mr./Mrs./Ms./Miss — optional, stored apart from `name`. */
  title?: string | null;
  name: string;
  mobile: string;
  gender: string;
  age?: string | number | null;
  dob?: string | null;
  address?: string | null;
  abha_number?: string | null;
  email?: string | null;
  /** Asked once; Vitals pre-fills from this column if already known. */
  blood_group?: string | null;
  emergency_contact?: string | null;
  emergency_mobile?: string | null;
  occupation?: string | null;
  marital_status?: string | null;
  nationality?: string | null;
}

export function createPatient(input: NewPatientInput): Promise<Patient> {
  return api.post('/patients/create', input);
}

export function searchPatients(q: string): Promise<Patient[]> {
  return api.get('/patients/search', { query: { q } });
}

/**
 * GET /api/patients — the full roster, bare array unless ?page= is sent (see
 * HMS_V4_API_CONTRACT.md §1). Used where a screen needs to resolve a bare
 * patient_id to a full record client-side (e.g. Appointments' "Start intake"
 * button) — a real, present need reproduced from AppointmentsModule.jsx's
 * getAllPatients() call, not a new pattern introduced here.
 */
export function fetchAllPatients(): Promise<Patient[]> {
  return api.get('/patients');
}

// ── AI patient summary ──────────────────────────────────────────
//
// GET .../summary and POST .../summary/generate deliberately do NOT go
// through the typed `api` client above: a governance block on the generate
// call comes back as `{error, code: 'AI_CONSENT_REQUIRED'}`, and that code
// isn't in lib/api/client.ts's ApiErrorCode set — normalizeCode() would
// silently collapse it to the generic 'forbidden', indistinguishable from
// any other 403. features/ai-assist/streamGeneration.ts hit the exact same
// problem for the sibling generate-document endpoint and solved it the same
// way: a small dedicated fetch (authFetch from @/api, for the Authorization/
// branch headers) and an Error subclass that keeps the raw code.
export class SummaryError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'SummaryError';
    this.code = code;
  }
}

export interface PatientSummaryData {
  summary: string | null;
  generated_at: string | null;
  stale: boolean;
  source_count: number;
}

async function summaryReq(method: 'GET' | 'POST', path: string, body?: unknown): Promise<PatientSummaryData> {
  const res = await authFetch(`/api/patients${path}`, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new SummaryError(payload.error || `Request failed (${res.status})`, payload.code);
  return payload as PatientSummaryData;
}

/** Cached only — never triggers an AI call. */
export function getPatientSummary(uhid: string): Promise<PatientSummaryData> {
  return summaryReq('GET', `/${uhid}/summary`);
}

/**
 * `model` only — the backend ignores a client-supplied `provider` and always
 * reads its own configured default (`getDefaultAiProvider(db)`), same as
 * every other AI-generation route in this app (see ai.js's generate-document
 * for the same rule and its own audit-flagged reasoning).
 */
export function generatePatientSummary(uhid: string, model?: string): Promise<PatientSummaryData> {
  return summaryReq('POST', `/${uhid}/summary/generate`, { model });
}

// ── Deceased status (Communication spec §5.5) ───────────────────
// Patient-level and written only through these endpoints — Admin/Doctor, a
// mandatory reason, audited server-side (backend/utils/patientDeceased.js).

export function getDeceasedStatus(uhid: string): Promise<DeceasedStatus> {
  return api.get(`/patients/${encodeURIComponent(uhid)}/deceased`);
}

/** `deceasedAt` is an ISO instant — convert the local date/time before calling. */
export function recordDeceased(
  uhid: string,
  input: { deceasedAt: string; reason: string },
): Promise<Patient & { cancelled_appointments: number }> {
  return api.post(`/patients/${encodeURIComponent(uhid)}/deceased`, { deceased_at: input.deceasedAt, reason: input.reason });
}

/** Reverses a Deceased status recorded in error. The reason is mandatory. */
export function correctDeceased(uhid: string, reason: string): Promise<Patient> {
  return api.post(`/patients/${encodeURIComponent(uhid)}/deceased/correct`, { reason });
}
